/* eslint-disable max-lines */
import { Injectable } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, AbstractControl, ValidatorFn, Validators } from '@angular/forms';

import { isNullOrUndefined } from '@app/util/util';
import { TravelerPaxResponse, TravelerForm, TravelerPax } from '@pax/entities/traveler-pax';
import { User } from '@app/modules/core/interfaces/user';
import { TravelerTypes, TravelerInfoType } from '@pax/entities/traveler-info';
import { PaxType } from '@booking/entities/pax-type';
import { AirValidationResponse } from '@app/entities/air-validation.response';
import { CountriesVm } from '@booking/entities/countries';
import {
  GUEST_CHILDREN_RANGE_AGES, INFANTS_RANGE_AGES_IN_MONTH, PASSENGER_CHILDREN_RANGE_AGES, TRAVELER_FORM_INPUTS
} from '@booking/constants/forms.constants';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { TRAVELER_STATIC_ID_PREFIX } from '@pax/mappers/traveler-id.mapper';
import { PaxFormCountryService } from '@pax/services/pax-form-country.service';
import { PaxFormLoyaltyService } from '@pax/services/pax-form-loyalty.service';
import { FormValidationService } from '@reservation/services/form-validation.service';
import { FormInputsService } from '@reservation/services/form-inputs.service';
import { replaceDiacritics } from '@shared/directives/mappers/replace-diacritics';
import { formatTravelerDate } from '@booking/mappers/traveler-date.mapper';
import { BookingPaxTypeDocumentProps } from '@app/entities/booking-documents';
import { defaultPaxDocumentId, getPaxDocumentNameById, getPaxDocumentTypeById } from '@forms/mappers/form-document-input.mapper';
import { FormDocumentInputComponent } from '@forms/form-components/components/form-document-input/form-document-input.component';
import { InputRegex } from '@forms/form-text-input/entities/input-mask';
import { BookingFormGeneralErrorTypes } from '@app/entities/booking-form-general-error';
import { travelerAgeInMonths, travelerAgeInYears } from '@pax/mappers/traveler-age.mapper';
import { HotelProvider } from '@hotels/hotel-results/entities/hotel-searched';
import { InputDateComponent } from '@forms/form-components/components/input-date/input-date.component';
import { dobToDate } from '@ux/mappers/date.mapper';
import { dateValidator } from '@reservation/validators/date.validator';
import { setFormDateValue } from '@app/modules/forms/mappers/form-dob.mapper';

@Injectable()
export class PaxFormDataService {

  constructor(
    private formInputsService: FormInputsService,
    private paxFormLoyaltyService: PaxFormLoyaltyService,
    private paxFormCountryService: PaxFormCountryService,
    private formValidationService: FormValidationService
  ) { }

  public isBuses(travelerBookingProvider: TravelBookingType): boolean {
    return travelerBookingProvider === TravelBookingType.BUSES;
  }

  public isActivities(travelerBookingProvider: TravelBookingType): boolean {
    return travelerBookingProvider === TravelBookingType.ACTIVITIES;
  }

  public isStaticTravelerId(id: string): boolean {
    return id.startsWith(TRAVELER_STATIC_ID_PREFIX);
  }

  public hasPaxTravelerInfo(paxTraveler: TravelerPaxResponse): boolean {
    return !isNullOrUndefined(paxTraveler);
  }

  public hasUserInfo(user: User, travelerNumber: number, type: TravelerTypes): boolean {
    return !!user && travelerNumber === 0 && type === TravelerTypes.ADULTS;
  }

  public defaultFormData(
    paxTraveler: TravelerPaxResponse,
    user: User,
    travelerType: TravelerTypes,
    travelerNumber: number,
    countries: CountriesVm[],
    documents: BookingPaxTypeDocumentProps[],
    hasUserTraveler: number,
    validator: AirValidationResponse = null
  ): TravelerForm {
    const hasPaxTravelerInfo = this.hasPaxTravelerInfo(paxTraveler);
    const hasUserInfo = this.hasUserInfo(user, travelerNumber, travelerType);
    const defaultPaxDocument = defaultPaxDocumentId(documents, hasPaxTravelerInfo, hasUserInfo, paxTraveler, user);
    const defaultCountryCode = hasPaxTravelerInfo ? paxTraveler.nationality :
      this.paxFormCountryService.defaultCountry(countries, user);
    if (!hasPaxTravelerInfo) {
      return this.emptyTravelerFormData(
        user, travelerType, validator, hasUserInfo, defaultCountryCode, defaultPaxDocument, hasUserTraveler)
    } else {
      return {
        UserId: paxTraveler.user_id,
        PaxType: paxTraveler.pax_type,
        FirstName: paxTraveler.first_name || '',
        SecondName: paxTraveler.middle_name || '',
        LastName: paxTraveler.last_name || '',
        Gender: paxTraveler.gender,
        DocumentNumber: paxTraveler.document_number,
        FrequentFlyerAirline: this.paxFormLoyaltyService.getLoyaltyPax(paxTraveler).airline ||
          this.paxFormLoyaltyService.defaultAirline(validator),
        FrequentFlyer: this.paxFormLoyaltyService.getLoyaltyPax(paxTraveler).number || '',
        Nationality: defaultCountryCode,
        ConfigurationDocumentId: defaultPaxDocument,
        DOB: paxTraveler.dob,
        BonvoyNumber: paxTraveler.bonvoy_number || '',
      };
    }
  }

  public formControls(
    defaultFormData: TravelerForm,
    reservationDate: Date,
    travelerType: TravelerTypes,
    travelerInfoType: TravelerInfoType,
    travelerBookingProvider: TravelBookingType,
    disabledDocuments: boolean,
    bookingSource: HotelProvider,
    documents: BookingPaxTypeDocumentProps[],
    travelerAge: number | null = null
  ): { [key: string]: UntypedFormControl } {
    let form = {
      [TRAVELER_FORM_INPUTS.USER_ID]: new UntypedFormControl(defaultFormData.UserId),
      [TRAVELER_FORM_INPUTS.PAX_TYPE]: new UntypedFormControl(defaultFormData.PaxType),
      [TRAVELER_FORM_INPUTS.FIRST_NAME]: new UntypedFormControl(defaultFormData.FirstName,
        this.formValidationService.stringValidators),
      [TRAVELER_FORM_INPUTS.SECOND_NAME]: new UntypedFormControl(defaultFormData.SecondName,
        this.formValidationService.secondNameValidators),
      [TRAVELER_FORM_INPUTS.LAST_NAME]: new UntypedFormControl(defaultFormData.LastName,
        this.formValidationService.stringValidators),
      [TRAVELER_FORM_INPUTS.GENDER]: new UntypedFormControl(defaultFormData.Gender, this.formValidationService.stringValidators),
      [TRAVELER_FORM_INPUTS.FREQUENT_FLYER_AIRLINE]: new UntypedFormControl(defaultFormData.FrequentFlyerAirline, null),
      [TRAVELER_FORM_INPUTS.FREQUENT_FLYER]: new UntypedFormControl(defaultFormData.FrequentFlyer, null),
      [TRAVELER_FORM_INPUTS.NATIONALITY]: new UntypedFormControl(defaultFormData.Nationality,
        this.formValidationService.stringValidators),
      [TRAVELER_FORM_INPUTS.DOB]: new UntypedFormControl(
        setFormDateValue(defaultFormData.DOB),
        [
          this.birthdateGreaterOrLesserValidator(
            travelerType, reservationDate, travelerAge
          ),
          this.ageValidators(
            travelerType, reservationDate,
            this.isPassenger(travelerInfoType),
            this.isBuses(travelerBookingProvider),
            this.isActivities(travelerBookingProvider),
          ),
          Validators.required,
          Validators.pattern(InputRegex.DATE),
          dateValidator()
        ])
    };
    if (!disabledDocuments) {
      const configurationDocumentId = this.fetchConfigurationDocumentId(defaultFormData.ConfigurationDocumentId, documents);
      form = {
        ...form,
        [TRAVELER_FORM_INPUTS.DOCUMENT_ID]: new UntypedFormControl(configurationDocumentId, [Validators.required]),
        [TRAVELER_FORM_INPUTS.DOCUMENT_NUMBER]: new UntypedFormControl(defaultFormData.DocumentNumber,
          [...this.formValidationService.documentValidator, Validators.maxLength(15), Validators.pattern(InputRegex.NUMERIC)]),
      };
    } else {
      const documentNumber = defaultFormData.ConfigurationDocumentId ? defaultFormData.DocumentNumber : null;
      form = {
        ...form,
        [TRAVELER_FORM_INPUTS.DOCUMENT_ID]: new UntypedFormControl(
          defaultFormData.ConfigurationDocumentId, []
        ),
        [TRAVELER_FORM_INPUTS.DOCUMENT_NUMBER]: new UntypedFormControl(documentNumber, []),
      };
    }
    if (bookingSource === HotelProvider.MarriottBonvoy) {
      form = {
        ...form,
        [TRAVELER_FORM_INPUTS.BONVOY_NUMBER]: new UntypedFormControl(defaultFormData.BonvoyNumber, [Validators.required]),
      };
    }
    return form;
  }

  public deleteFormData(
    form: UntypedFormGroup, defaultFormData: TravelerForm,
    documentSelectorRef: FormDocumentInputComponent, dobInputRef: InputDateComponent,
    documents: BookingPaxTypeDocumentProps[]
  ): void {
    const defaultDocumentId = documents?.length === 1 ? defaultFormData.ConfigurationDocumentId : null;
    const documentType = getPaxDocumentTypeById(documents, defaultDocumentId);
    form?.reset({
      ...defaultFormData,
      [TRAVELER_FORM_INPUTS.FIRST_NAME]: '',
      [TRAVELER_FORM_INPUTS.SECOND_NAME]: '',
      [TRAVELER_FORM_INPUTS.LAST_NAME]: '',
      [TRAVELER_FORM_INPUTS.DOCUMENT_NUMBER]: '',
      [TRAVELER_FORM_INPUTS.DOCUMENT_ID]: defaultDocumentId,
      [TRAVELER_FORM_INPUTS.DOB]: '',
      [TRAVELER_FORM_INPUTS.GENDER]: '',
      [TRAVELER_FORM_INPUTS.FREQUENT_FLYER]: ''
    });
    if (documentSelectorRef) {
      documentSelectorRef.documentType = documentType;
      documentSelectorRef.defaultDocument = getPaxDocumentNameById(documents, defaultDocumentId);
      documentSelectorRef.onDocumentTypeSelected({ value: documentSelectorRef.defaultDocument, index: null });
    }
    dobInputRef?.reset();
  }

  public mapperPaxData(values: TravelerForm): TravelerPax {
    return values ? {
      user_id: values.UserId, pax_type: values.PaxType, first_name: values.FirstName,
      middle_name: values.SecondName, last_name: values.LastName, gender: values.Gender,
      document_number: values.DocumentNumber, configuration_document_id: values.ConfigurationDocumentId,
      nationality: values.Nationality, dob: formatTravelerDate(dobToDate(values.DOB)),
    } : {} as TravelerPax;
  }

  public changeUpperCaseName(e: Event, type: string, form: UntypedFormGroup): void {
    const value = e.target['value'];
    switch (type) {
      case TRAVELER_FORM_INPUTS.FIRST_NAME:
        form.patchValue({ [TRAVELER_FORM_INPUTS.FIRST_NAME]: this.formInputsService.capitalizeValue(value) });
        break;
      case TRAVELER_FORM_INPUTS.SECOND_NAME:
        form.patchValue({ [TRAVELER_FORM_INPUTS.SECOND_NAME]: this.formInputsService.capitalizeValue(value) });
        break;
      case TRAVELER_FORM_INPUTS.LAST_NAME:
        form.patchValue({ [TRAVELER_FORM_INPUTS.LAST_NAME]: this.formInputsService.capitalizeValue(value) });
        break;
      default:
        break;
    }
  }

  private birthdateGreaterOrLesserValidator(
    traveler: TravelerTypes, reservationDate: Date, travelerAge: number | null
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean | string } | null => {
      const years = travelerAgeInYears(control.value, reservationDate);
      const validValue = !isNaN(years);
      if (traveler !== TravelerTypes.ADULTS && validValue && !isNullOrUndefined(travelerAge)) {
        if (years > travelerAge) {
          return {
            [BookingFormGeneralErrorTypes.BIRTHDATE_GREATER_THAN_ALLOWED]: true, type: traveler, traveler
          };
        } else if (years < travelerAge) {
          return {
            [BookingFormGeneralErrorTypes.BIRTHDATE_LESSER_THAN_ALLOWED]: true, type: traveler, traveler
          };
        } else {
          return null;
        }
      } else {
        return null;
      }
    }
  }

  private ageValidators(
    traveler: TravelerTypes,
    reservationDate: Date,
    isPassenger: boolean,
    noRestrictAdultAge: boolean,
    noRestrictChildrenAndInfantAges: boolean,
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean | string } | null => {
      const agesLimiters = isPassenger ? PASSENGER_CHILDREN_RANGE_AGES : GUEST_CHILDREN_RANGE_AGES;
      const adultsMax = noRestrictAdultAge ? 0 : agesLimiters.MAX;
      switch (traveler) {
        case TravelerTypes.ADULTS:
          if (noRestrictAdultAge) {
            if (isNaN(travelerAgeInYears(control.value, reservationDate)) ||
              travelerAgeInYears(control.value, reservationDate) < adultsMax) {
              return { [BookingFormGeneralErrorTypes.AGE_RANGE]: true, type: TravelerTypes.ADULTS, traveler };
            }
          } else if (isNaN(travelerAgeInYears(control.value, reservationDate)) ||
            travelerAgeInYears(control.value, reservationDate) <= adultsMax) {
            return { [BookingFormGeneralErrorTypes.AGE_RANGE]: true, type: TravelerTypes.ADULTS, traveler };
          }
          return null;
        case TravelerTypes.CHILDREN:
          // Temporary logic, delete when we receive MinAge and MaxAge in Validate
          if (noRestrictChildrenAndInfantAges) {
            return null;
          }
          if (!isNullOrUndefined(control.value) && (isNaN(travelerAgeInYears(control.value, reservationDate)) ||
            (travelerAgeInYears(control.value, reservationDate) < agesLimiters.MIN ||
              travelerAgeInYears(control.value, reservationDate) > agesLimiters.MAX))) {
            return { [BookingFormGeneralErrorTypes.AGE_RANGE]: true, type: TravelerTypes.CHILDREN, traveler };
          }
          return null;
        case TravelerTypes.INFANTS:
          // Temporary logic, delete when we receive MinAge and MaxAge in Validate
          if (noRestrictChildrenAndInfantAges) {
            return null;
          }
          if (!isNullOrUndefined(control.value) && (isNaN(travelerAgeInMonths(control.value, reservationDate)) ||
            (travelerAgeInMonths(control.value, reservationDate) < INFANTS_RANGE_AGES_IN_MONTH.MIN ||
              travelerAgeInMonths(control.value, reservationDate) > INFANTS_RANGE_AGES_IN_MONTH.MAX))) {
            return { [BookingFormGeneralErrorTypes.AGE_RANGE]: true, type: TravelerTypes.INFANTS, traveler };
          }
          return null;
        default:
          return null;
      }
    };
  }

  private isPassenger(travelerInfoType: TravelerInfoType): boolean {
    return travelerInfoType === TravelerInfoType.PASSENGER;
  }

  private emptyTravelerFormData(
    user: User, travelerType: TravelerTypes, validator: AirValidationResponse, 
    hasUserInfo: boolean, defaultCountryCode: string, defaultPaxDocument: number, hasUserTraveler: number): TravelerForm {
    let hasUserInfoInPaxList = hasUserInfo && hasUserTraveler < 0 ;
    return {
      UserId: user.id,
      PaxType: PaxType[travelerType],
      FirstName: hasUserInfoInPaxList ? this.formInputsService.userFirstName(user) : '',
      SecondName: hasUserInfoInPaxList ? this.formInputsService.userSecondName(user) : '',
      LastName: hasUserInfoInPaxList ? replaceDiacritics(user.last_name) : '',
      Gender: hasUserInfoInPaxList ? this.formInputsService.userGender(user.gender) : '',
      DocumentNumber: hasUserInfoInPaxList ? user.identification : '',
      FrequentFlyerAirline: this.paxFormLoyaltyService.defaultAirline(validator),
      FrequentFlyer: '',
      Nationality: defaultCountryCode,
      ConfigurationDocumentId: defaultPaxDocument,
      DOB: hasUserInfoInPaxList ? user.birth_date : '',
      BonvoyNumber: '',
    };
  }

  private fetchConfigurationDocumentId(configurationDocumentId: number, documents: BookingPaxTypeDocumentProps[]): number {
    if (configurationDocumentId) {
      return configurationDocumentId
    } else if (documents?.length > 0) {
      return documents[0].DOCUMENT_ID
    } else {
      return null;
    }
  }
}
